import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Import all section components
import HeroSection from '../components/home/HeroSection';
import TechStack from '../components/home/TechStack';
import WhyChooseSection from '../components/home/WhyChooseSection';
import SpecializationSection from '../components/home/SpecializationSection';
import ServicesSection from '../components/home/ServicesSection';
import StatsSection from '../components/home/StatsSection';
import TestimonialsSection from '../components/home/TestimonialsSection';
import MissionSection from '../components/home/MissionSection';
import CTASection from '../components/home/CTASection';
import ScrollProgress from '../components/ScrollProgress';
import PartnersSection from '../components/home/PartnersSection';
import CategoriesSection from '../components/home/CategoriesSection';
import ProductsSection from '../components/home/ProductsSection';
import ParticleBackground from '../components/home/ParticleBackground';
import SEO from '../components/SEO';


gsap.registerPlugin(ScrollTrigger);


const Home = () => {
  const heroRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    // Standard trigger for any animations if needed, but removing pinning for a simple look
    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MehareTech",
    "url": "https://meharetech.com",
    "logo": "https://meharetech.com/images/logo-meharetech.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-8708604830",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": "en"
    },
    "sameAs": [
      "https://www.instagram.com/meharetech_/",
      "https://www.linkedin.com/company/meharetech"
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "MehareTech",
    "url": "https://meharetech.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://meharetech.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "MehareTech Development Services",
    "image": "https://meharetech.com/images/logo-meharetech.png",
    "url": "https://meharetech.com/services",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "serviceType": ["Website Development", "Android App Development", "SEO", "Digital Marketing"]
  };

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Full-Stack Web & Android Development Bootcamp",
    "description": "Professional coding courses by MehareTech to transform your career in technology.",
    "provider": {
      "@type": "Organization",
      "name": "MehareTech",
      "sameAs": "https://meharetech.com"
    }
  };

  return (
    <div className="bg-white transition-colors duration-500 relative">
      <SEO
        title="India's Best Website & Android Development Agency | Learn SEO & Digital Marketing"
        description="MehareTech is the top-rated IT agency for high-end Website Development, Android App Development, and SEO. Join our coding courses to master Digital Marketing and Programming."
        keywords="website development india, android app development company, SEO experts, digital marketing agency, learn coding online, web development courses"
        schema={[homeSchema, websiteSchema, serviceSchema, courseSchema]}
      />

      {/* Global High-End WebGL Particle Background */}
      <ParticleBackground />

      {/* Scroll Progress Bar */}
      <ScrollProgress />

      {/* Hero Section */}
      <HeroSection heroRef={heroRef} />

      {/* Services Section */}
      <ServicesSection />

      {/* Categories Section */}
      <CategoriesSection />

      {/* Why Choose Section */}
      <WhyChooseSection />

      {/* Specialization Section */}
      <SpecializationSection />

      {/* Tech Stack Section */}
      <TechStack />

      {/* Stats Section */}
      <StatsSection statsRef={statsRef} />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Mission Section */}
      <MissionSection />

      {/* Partners Section */}
      <PartnersSection />

      {/* CTA Section */}
      <CTASection />

    </div>
  );
};

export default Home;
